import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, message } from 'antd';
import { StyledDrawer, StyledFooter, StyledDrawerTitle } from './style';
import { Button } from '../../Button';
import { TextArea } from '../../TextArea';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { Label } from 'ui/Label';
import EnumSelect from '../../../admin/Selects/EnumSelect';

interface DrawerCancelListingProps {
  open: boolean;
  onClose: () => void;
  listingId: string;
  setReload: React.Dispatch<React.SetStateAction<number>>;
  onSuccess?: () => void;
}

interface CancelValues {
  cancel_reason: string;
  cancel_reason_description?: string;
}

export const DrawerCancelListing = ({
  open,
  onClose,
  listingId,
  setReload,
  onSuccess,
}: DrawerCancelListingProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [selectedCancelType, setSelectedCancelType] = useState<string>('');
  const [listingTitle, setListingTitle] = useState<string>('');

  useQueryApiClient({
    request: {
      url: `/api/v2/listings/${listingId}`,
      method: 'GET',
    },
    enabled: !!listingId && open,
    onSuccess: (data: { title?: string }) => {
      setListingTitle(data?.title || '');
    },
  });

  const { appendData: cancelListing, isLoading: isCancelling } =
    useQueryApiClient({
      request: {
        url: `/api/v2/listings/${listingId}/cancel`,
        method: 'POST',
      },
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'listings.cancel_success' }));
        form.resetFields();
        setSelectedCancelType('');
        setReload((old) => old + 1);
        onClose();
        onSuccess?.();
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'listings.cancel_error' }));
      },
    });

  const handleCancelTypeChange = (value: string) => {
    setSelectedCancelType(value);
  };

  const handleSubmit = (values: CancelValues) => {
    const payload = {
      cancel_reason: values.cancel_reason,
      cancel_reason_description: values.cancel_reason_description || null,
    };
    cancelListing(payload);
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedCancelType('');
    onClose();
  };

  const isOtherTypeSelected = selectedCancelType === 'cancelled_other';

  return (
    <StyledDrawer
      open={open}
      onClose={handleClose}
      title={
        <StyledDrawerTitle>
          <Label label={intl.formatMessage({ id: 'listings.cancel' })} />
          <Label label={listingTitle} title={true} />
        </StyledDrawerTitle>
      }
      width="50%"
      destroyOnClose
      className="styled-drawer"
      footer={
        <StyledFooter>
          <Button
            type="default"
            onClick={handleClose}
            disabled={isCancelling}
            label={intl.formatMessage({ id: 'general.cancel' })}
          />
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={isCancelling}
            disabled={false}
            label={intl.formatMessage({ id: 'listings.cancel_confirm' })}
          />
        </StyledFooter>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={false}
      >
        <EnumSelect
          label={intl.formatMessage({ id: 'listings.cancel_reason' })}
          name="cancel_reason"
          onChange={handleCancelTypeChange}
          code="ListingCancelType"
          validations={['required']}
        />

        {isOtherTypeSelected && (
          <TextArea
            name="cancel_reason_description"
            placeholder={intl.formatMessage({
              id: 'listings.cancel_reason_description_placeholder',
            })}
            maxLength={2000}
            validations={['required']}
          />
        )}
      </Form>
    </StyledDrawer>
  );
};
